import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { Modal } from "@mui/material"
import Button from "../Button/Button"
import Input from "../Input/Input"
import Dropdown from "../Dropdown/Dropdown"
import Calendar from "../Calendar/Calendar"
import CoApplicantSearch from "../CoApplicantSearch/CoApplicantSearch"
import { createCoApplicant } from "../../features/coApplicants/coApplicants"
import { createContactLink } from "../../features/contactLinks/contactLinks"
import { showSnackBar } from "../Snackbar/Snackbar"
import { generateSSNumber, phoneNumber, ssnForPrefill } from "../../utils/payment"

const AddCoApplicantModal = ({ open, onClose, userId, company, onSuccess }) => {
  const dispatch = useDispatch()
  const [mode, setMode] = useState('create')
  const [linkedId, setLinkedId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [info, setInfo] = useState({
    firstName: '', lastName: '', middleName: '', email: '',
    primaryPhone: '', ssNumber: '', dateOfBirth: '', gender: '',
    address: '', aptNumber: '', city: '', state: '', zip: '',
  })

  const emptyInfo = {
    firstName: '', lastName: '', middleName: '', email: '',
    primaryPhone: '', ssNumber: '', dateOfBirth: '', gender: '',
    address: '', aptNumber: '', city: '', state: '', zip: '',
  }

  const handleChange = (field, value) => {
    if (field === 'primaryPhone') value = phoneNumber(value)
    if (field === 'ssNumber') value = generateSSNumber(value)
    if (['firstName', 'lastName', 'middleName'].includes(field)) {
      value = value?.replace(/[^a-zA-Z']/g, '')
    }
    setInfo(prev => ({ ...prev, [field]: value }))
  }

  const handleSelectExisting = (coApplicant) => {
    setLinkedId(coApplicant._id)
    setInfo({
      firstName: coApplicant.firstName || '',
      lastName: coApplicant.lastName || '',
      middleName: coApplicant.middleName || '',
      email: coApplicant.email || '',
      primaryPhone: coApplicant.primaryPhone ? phoneNumber(coApplicant.primaryPhone) : '',
      ssNumber: ssnForPrefill(coApplicant.ssNumber),
      dateOfBirth: coApplicant.dateOfBirth || '',
      gender: coApplicant.gender || '',
      address: coApplicant.address?.address || '',
      aptNumber: coApplicant.address?.aptNumber || '',
      city: coApplicant.address?.city || '',
      state: coApplicant.address?.state || '',
      zip: coApplicant.address?.zip || '',
    })
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      let coAppId = linkedId
      if (mode === 'create') {
        const res = await dispatch(createCoApplicant({
          firstName: info.firstName,
          lastName: info.lastName,
          email: info.email,
          primaryPhone: info.primaryPhone?.replace(/\D/g, ''),
          ssNumber: info.ssNumber?.replace(/\D/g, ''),
          dateOfBirth: info.dateOfBirth,
          middleName: info.middleName,
          gender: info.gender,
          address: { address: info.address, aptNumber: info.aptNumber, city: info.city, state: info.state, zip: info.zip },
          company,
        }))
        if (res.error) {
          showSnackBar('error', res.payload || 'Failed to create co-applicant')
          setLoading(false)
          return
        }
        coAppId = res.payload?._id
      }
      const linkRes = await dispatch(createContactLink({
        primaryContact: userId,
        linkedContact: coAppId,
        linkType: 'co-applicant',
        company,
      }))
      if (linkRes.error) {
        showSnackBar('error', linkRes.payload || 'Failed to link co-applicant')
      } else {
        showSnackBar('success', 'Co-applicant added')
        onSuccess()
        onClose()
      }
    } catch (error) {
      showSnackBar('error', 'Something went wrong')
    }
    setLoading(false)
  }

  const isReadonly = mode === 'link' && !!linkedId
  const canSubmit = mode === 'link' ? !!linkedId : (info.firstName && info.lastName && info.dateOfBirth)

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <div className="flex items-center justify-center h-screen">
        <div className="bg-surface rounded-2xl flex flex-col min-w-[680px] max-h-[90vh]">
          <div className="px-6 pt-6 flex justify-between items-center">
            <h4 className="font-bold text-xl-20">Add Co-Applicant</h4>
            <div className="cursor-pointer text-text-secondary hover:text-text-primary text-xl" onClick={onClose}>&#x2715;</div>
          </div>
          <div className="w-full h-px bg-divider mt-4" />
          <div className="px-6 py-6 flex-1 overflow-visible">
            <div className="flex gap-4 mb-6">
              <Button size="sm" type={mode === 'create' ? "contained" : "outlined"}
                onClick={() => { setMode('create'); setLinkedId(null); setInfo(emptyInfo) }}>
                Create New
              </Button>
              <Button size="sm" type={mode === 'link' ? "contained" : "outlined"}
                onClick={() => { setMode('link'); setLinkedId(null); setInfo(emptyInfo) }}>
                Link Existing
              </Button>
            </div>
            {mode === 'link' && (
              <div className="">
                <CoApplicantSearch company={company} onSelect={handleSelectExisting} />
              </div>
            )}
            {(mode === 'create' || linkedId) && (
              <>
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <Input label="First name *" value={info.firstName} readOnly={isReadonly}
                      onChange={(e) => handleChange('firstName', e.target.value)} />
                  </div>
                  <div className="flex-1">
                    <Input label="Last name *" value={info.lastName} readOnly={isReadonly}
                      onChange={(e) => handleChange('lastName', e.target.value)} />
                  </div>
                </div>
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <Input label="Email" value={info.email} readOnly={isReadonly}
                      onChange={(e) => handleChange('email', e.target.value)} />
                  </div>
                  <div className="flex-1">
                    <Input label="Phone" value={info.primaryPhone} readOnly={isReadonly}
                      onChange={(e) => handleChange('primaryPhone', e.target.value)} />
                  </div>
                </div>
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <Input label="SSN" value={info.ssNumber} readOnly={isReadonly}
                      onChange={(e) => handleChange('ssNumber', e.target.value)} />
                  </div>
                  <div className="flex-1">
                    <Calendar label="Date of Birth *" value={info.dateOfBirth} disableFuture={true}
                      disabled={isReadonly} onChange={(e) => handleChange('dateOfBirth', e)} />
                  </div>
                </div>
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <Dropdown label="Gender" value={info.gender} disabled={isReadonly}
                      menuItems={[{ label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }]}
                      setValue={(e) => handleChange('gender', e)} />
                  </div>
                  <div className="flex-1" />
                </div>
              </>
            )}
          </div>
          <div className="px-6 pb-6 flex justify-end gap-4">
            <Button type="outlined" onClick={onClose}>Cancel</Button>
            <Button disabled={!canSubmit || loading} onClick={handleSubmit}>
              {loading ? 'Adding...' : 'Add Co-Applicant'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default AddCoApplicantModal
